/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */

import {useCallback, useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';

interface UseDeviceViewErrorMessageResult {
  doShowErrorMessage: boolean;
  errorMessage: string;
  clearErrorMessage: () => void;
}

const NO_ERROR_MESSAGE = '';

/**
 * Custom hook for handling error-message logic used by device view/details
 * tabs.
 * 
 * ```typescript
 * 
 *   import {useDeviceViewErrorMessage} from '../../hooks/useDeviceViewErrorMessage';
 *   import ErrorSnackbar from '@ion/dls/src/components/snackbars/ErrorSnackbar';
 * 
 *   // Calling the hook itself
 *   const {
 *     doShowErrorMessage,
 *     errorMessage,
 *     clearErrorMessage
 *   } = useDeviceViewErrorMessage([
 *     [isCurrentFileHistoryError, 'admin-devices:view.deviceDetailSummary.fileHistoryErrorMessage'],
 *     [isManufacturingInfoError, 'admin-devices:view.deviceDetailSummary.manufacturingInfoErrorMessage']
 *   ]);
 * 
 *   // Then using the hook's return values, e.g. to show an error snackbar
 *   <ErrorSnackbar show={doShowErrorMessage}
 *     message={errorMessage}
 *     onDismiss={clearErrorMessage} />
 * 
 * ```
 * 
 * @param   errorData An array of tuples. Each tuple's first element is the 
 *          error state, and each tuple's second element is the error message's
 *          locale path to be translated.
 * @returns An object with the following properties:
 *          `errorMessage` - The current error message.
 *          `clearErrorMessage` - a callback that resets the current error 
 *          message.
 */
export function useDeviceViewErrorMessage(errorData: [boolean, string][]): UseDeviceViewErrorMessageResult {
  const [errorMessage, setErrorMessage] = useState(NO_ERROR_MESSAGE);

  const errorStates = errorData.map(arg => arg?.[0]);

  useEffect(() => {
    if (errorMessage === NO_ERROR_MESSAGE) {
      for (const [isError, errorMessageLocalePath] of errorData) {
        if (isError) {
          setErrorMessage(translate(errorMessageLocalePath));
          break;
        }
      }
    }
  }, errorStates);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage(NO_ERROR_MESSAGE);
  }, []);

  return {
    doShowErrorMessage : errorMessage !== NO_ERROR_MESSAGE,
    errorMessage,
    clearErrorMessage
  };
}
